import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, Subscription } from 'rxjs';
import { statusesAllowingAcknowledgement, statusDisplayMapping } from '../referrals.model';
import { GetPatient, GetReferral, ReferralStatus } from 'src/app/api-module/control-centre-api-client';
import { ReferralsUtility } from '../referrals.utility';
import { ViewReferralComponent } from '../view-referral/view-referral.component';
import { firstBy } from 'thenby';
import { DateTime } from 'luxon';
import { ReferralsService } from '../referrals.service';

@Component({
    selector: 'app-referral-table',
    templateUrl: './referral-table.component.html',
    styleUrls: ['referral-table.component.scss']
})
export class ReferralTableComponent implements OnChanges, OnDestroy {
    INITIAL_PAGE_SIZE = 15;

    @Input()
    referralList: GetReferral[] | null;

    @Input()
    filtersApplied: boolean;

    @Output()
    visualRowSizeChanged = new EventEmitter<number>();

    @Output()
    rowActioned = new EventEmitter<void>();

    @ViewChild(MatSort) sort!: MatSort;

    @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;

    statusDisplayMapping = statusDisplayMapping;
    referralStatus = ReferralStatus;

    displayedColumns: string[] = [
        'urgency',
        'modality',
        'status',
        'mrn',
        'patientName',
        'timeOfReferral',
        'timeOfDecision',
        'referrer',
        'action'
    ];
    dataSource = new MatTableDataSource<GetReferral>([]);
    selectedItem = new BehaviorSubject<GetReferral>(null);

    private readonly subscription = new Subscription();

    get showLoadMore(): boolean {
        return this.referralList?.length > this.paginator?.pageSize;
    }

    constructor(private readonly dialog: MatDialog, private readonly referralsService: ReferralsService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.referralList) {
            const currentValue = changes.referralList.currentValue;

            // Transform data to include combined modality
            const transformedData = (currentValue ?? []).map(referral => ({
                ...referral,
                modality: this.combineModality(referral)
            }));

            // sort control on UI
            this.dataSource.sort = this.sort;
            // custom sort logic
            this.dataSource.sortData = (data: GetReferral[], sort: MatSort) => {
                switch (sort.direction) {
                    case 'asc':
                        return this.sortReferrals(data, sort.active, true);

                    case 'desc':
                        return this.sortReferrals(data, sort.active, false);

                    default:
                        // Clear sorting scenario
                        return this.sortReferrals(data); // Default sorting
                }
            };

            // pagination
            this.dataSource.paginator = this.paginator;

            if (currentValue === undefined || currentValue === null) {
                this.dataSource.data = [];
                this.paginator.length = 0;
                this.emitVisualRowSize(0, 0);
            } else {
                this.dataSource.data = transformedData;
                this.paginator.length = transformedData.length;
                this.emitVisualRowSize(transformedData.length, this.paginator.pageSize ?? this.INITIAL_PAGE_SIZE);
            }
        }
    }

    private combineModality(referral: GetReferral): string {
        const bodyParts = referral.finalisedBodyParts?.length ? referral.finalisedBodyParts : referral.requestedBodyParts || [];

        const modalities = bodyParts.map(bodyPart => bodyPart.modality);

        return Array.from(new Set(modalities)).join('|');
    }

    displayDateTime(dateTime: string): string {
        return ReferralsUtility.DisplayDateTime(dateTime, true);
    }

    getUrgencyText(row: GetReferral): string {
        return ReferralsUtility.GetUrgencyText(row);
    }

    getStatusText(status: ReferralStatus): string {
        return ReferralsUtility.GetStatusText(status);
    }

    displayPersonName(person: GetPatient | null | undefined): string {
        return ReferralsUtility.DisplayPersonName(person);
    }

    isReferralAcknowledgable(status: string): boolean {
        return statusesAllowingAcknowledgement.includes(ReferralStatus[status]);
    }

    loadMore(): void {
        this.paginator.pageSize = this.paginator.pageSize + 10;
        this.dataSource._updateChangeSubscription();
        this.emitVisualRowSize(this.referralList.length, this.paginator.pageSize);
    }

    emitVisualRowSize(rowSize: number, pageSize: number): void {
        this.visualRowSizeChanged.emit(Math.min(rowSize, pageSize));
    }

    openDetails(row: GetReferral): void {
        const dialogConfig = ReferralsUtility.getViewReferralMatDialogConfig({ referralId: row.id });

        const dialogRef = this.dialog.open(ViewReferralComponent, dialogConfig);
        this.selectedItem.next(row);
        this.subscription.add(
            dialogRef.afterClosed().subscribe((isAcknowledged: boolean) => {
                this.selectedItem.next(null);
                if (isAcknowledged) {
                    this.rowActioned.emit();
                }
            })
        );
    }

    getClass(row: GetReferral): { [key: string]: boolean } {
        const cssClass = { 'selected-row': this.selectedItem.value?.id === row.id };
        switch (this.getUrgencyText(row)) {
            case 'Stroke':
            case 'Trauma':
            case 'Urgent':
            case 'Trauma, Stroke':
                cssClass['urgency-urgent'] = true;
                break;
        }
        return cssClass;
    }

    sortReferrals(rows: GetReferral[], fieldName = 'timeOfReferral', isAsc = false): GetReferral[] {
        return rows.sort(
            firstBy((left: GetReferral, right: GetReferral) => {
                if (fieldName === 'urgency') {
                    return this.urgencyCompare(this.getUrgencyText(left), this.getUrgencyText(right), isAsc);
                } else if (fieldName === 'status') {
                    return this.commonCompare(this.getStatusText(left.status), this.getStatusText(right.status), isAsc);
                } else if (fieldName === 'patientName') {
                    return this.commonCompare(
                        this.displayPersonName(left.patient).toLowerCase(),
                        this.displayPersonName(right.patient).toLowerCase(),
                        isAsc
                    );
                } else if (fieldName === 'timeOfReferral') {
                    // Switch isAsc here as we want newest first by default.
                    return this.dateTimeCompare(left.processInitiatedAt, right.processInitiatedAt, !isAsc);
                } else if (fieldName === 'timeOfDecision') {
                    // Switch isAsc here as we want newest first by default.
                    return this.dateTimeCompare(left.timeOfDecision, right.timeOfDecision, !isAsc);
                } else {
                    return this.commonCompare(left[fieldName], right[fieldName], isAsc);
                }
            }).thenBy((left: GetReferral, right: GetReferral) =>
                // Process Initiated At newest first
                this.dateTimeCompare(left.processInitiatedAt, right.processInitiatedAt, true)
            )
        );
    }

    dateTimeCompare(left: string, right: string, isAsc: boolean): number {
        const leftTime = DateTime.fromISO(left);
        const rightTime = DateTime.fromISO(right);

        let sortValue = 0;

        if (leftTime.isValid && rightTime.isValid) {
            if (leftTime !== rightTime) {
                sortValue = leftTime > rightTime ? 1 : -1;

                if (isAsc) {
                    sortValue *= -1;
                }
            }
        } else if (leftTime.isValid) {
            sortValue = -1;
        } else if (rightTime.isValid) {
            sortValue = 1;
        }

        return sortValue;
    }

    urgencyCompare(left: string, right: string, isAsc: boolean): number {
        const leftPriority = this.urgencyPriority(left);
        const rightPriority = this.urgencyPriority(right);
        return this.commonCompare(leftPriority, rightPriority, isAsc);
    }

    commonCompare(left: unknown, right: unknown, isAsc: boolean): number {
        // If there are data in the column that can be converted to a number,
        // it must be ensured that the rest of the data
        // is of the same type so as not to order incorrectly.
        const leftType = typeof left;
        const rightType = typeof right;

        if (leftType !== rightType) {
            if (leftType === 'number') {
                left += '';
            }
            if (rightType === 'number') {
                right += '';
            }
        }

        // If both left and right exist (truthy), then compare the two. Otherwise, check if
        // one value exists while the other doesn't. In this case, existing value should come last.
        // This avoids inconsistent results when comparing values to undefined/null.
        // If neither value exists, return 0 (equal).
        let comparatorResult = 0;
        if (left != null && right != null) {
            // Check if one value is greater than the other; if equal, comparatorResult should remain 0.
            if (left > right) {
                comparatorResult = 1;
            } else if (left < right) {
                comparatorResult = -1;
            }
        } else if (left != null) {
            comparatorResult = 1;
        } else if (right != null) {
            comparatorResult = -1;
        }

        return comparatorResult * (isAsc ? 1 : -1);
    }

    // More urgent = higher priority when sorting by urgency
    urgencyPriority(urgency: string): number {
        switch (urgency) {
            case 'Trauma, Stroke':
                return 3;
            case 'Stroke':
                return 2;
            case 'Trauma':
                return 1;
            default:
                return 0;
        }
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }
}
